import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import da from 'date-fns/locale/da';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { KoreseddelCargo, KoreseddelTemplate } from '../../types';
import SplitCargoDialog from '../../components/common/SplitCargoDialog';
import type { PlaceResult } from '../../components/common/PlacesAutocomplete';

// Danish translations for cargo types
const CARGO_TYPE_TRANSLATIONS: Record<string, string> = {
  'WASTE': 'Affald',
  'SERVICE': 'Service',
  'DELIVERY': 'Levering',
  'COLLECTION': 'Afhentning',
  'MISCELLANEOUS': 'Diverse',
  'PALLET_GOODS': 'Pallegods',
  'GENERAL_CARGO': 'Stykgods',
  'COOL': 'Køl',
  'FREEZE': 'Frost',
  'CC': 'Temperaturkontrolleret',
  'PHARMA': 'Pharma',
  'BULK_CARGO': 'Bulk',
  'CONCRETE': 'Beton',
  'PACKAGES': 'Pakker',
  'MACHINES': 'Maskiner',
  'CARS': 'Biler',
  'BOATS': 'Både',
  'LIVE_ANIMALS': 'Levende dyr',
  'COIL': 'Coils',
  'SLUDGE': 'Slam',
  'MODULE': 'Modul',
  'LIQUID': 'Væske',
  'BIG_BAGS': 'Big Bags',
  'FLANGERS': 'Flanger',
  'CONTAINER': 'Container',
  '8_FEET': '8 fod',
  '10_FEET': '10 fod',
  '20_FEET': '20 fod',
  '20_FEET_HIGH_CUBE': '20 fod HC',
  '40_FEET': '40 fod',
  '40_FEET_HIGH_CUBE': '40 fod HC',
  'CRANE_WORK': 'Kranarbejde',
  '4_AXLE_RIGID_HGV': '4-akslet lastbil',
  '4_AXLE_CRANE': '4-akslet kran',
  '7_AXLE_CENTRE_TRAILER': '7-akslet centertrailer',
  '7_AXLE_TRAILER': '7-akslet trailer',
  '7_AXLE_CRANE': '7-akslet kran',
  '3_AXLE_TRUCK': '3-akslet lastbil',
  '5_AXLE_TIPPER_TRAILER': '5-akslet tiptrailer',
  '7_AXLE_TIPPER_TRAILER': '7-akslet tiptrailer',
  'LOW_BED_TRAILER': 'Lavlæsser',
  'STRAIGHT_EXTENDABLE_TRAILER': 'Udtræksstrailer',
  'WAREHOUSE': 'Lager',
  'WORKSHOP': 'Værksted',
  'SERVICE_VAN': 'Servicevogn',
  'FTL': 'Heltransport',
  'LTL': 'Stykgods',
  'PTL': 'Deltransport',
};

function translateCargoType(type: string | undefined): string {
  if (!type) return '-';
  return CARGO_TYPE_TRANSLATIONS[type.toUpperCase()] || type;
}

// Check if cargo is part of a split by parsing the note field
// Single cargo split: "X/3 - REF2, REF3 | Original rute: BY - BY"
// Route split: "Rute-split X/3 - REF2, REF3 | Rute: NAME"
function isSplitCargo(note: string | null): boolean {
  if (!note) return false;
  return /^(?:Rute-split\s+)?\d+\/\d+\s*-\s*[^|]+\|\s*(?:Original rute|Original|Rute):/.test(note);
}

function isArchiveCopy(note: string | null): boolean {
  if (!note) return null as unknown as boolean;
  const match = note.match(/^(?:Rute-split\s+)?(\d+)\/(\d+)\s*-/);
  return match !== null && parseInt(match[1]) === 1;
}

// Sorting types
type SortField = 'ref' | 'type' | 'description' | 'customerName' | 'invoiceRef' | 'from' | 'to' | 'date' | 'lines';
type SortDirection = 'asc' | 'desc';

export default function KoreseddelList() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCargos, setSelectedCargos] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Date range - default to 7 days back and 7 days forward
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [toDate, setToDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  });

  // Menu state for template selection
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuCargo, setMenuCargo] = useState<KoreseddelCargo | null>(null);

  // Bulk menu state
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);

  // Split dialog state
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitCargo, setSplitCargo] = useState<KoreseddelCargo | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Filter state
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');

  // Format dates for API
  const fromStr = fromDate?.toISOString().split('T')[0];
  const toStr = toDate?.toISOString().split('T')[0];

  const { data: cargos, isLoading, error } = useQuery({
    queryKey: ['koreseddel-cargos', fromStr, toStr],
    queryFn: () => api.getKoreseddelCargos(fromStr, toStr),
  });

  const { data: templates } = useQuery({
    queryKey: ['koreseddel-templates'],
    queryFn: () => api.getKoreseddelTemplates(),
  });

  const generateMutation = useMutation({
    mutationFn: ({ cargoId, templateId }: { cargoId: string; templateId: string }) =>
      api.generateKoreseddel(cargoId, templateId),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['koreseddel-cargos'] });
      queryClient.invalidateQueries({ queryKey: ['koresedler'] });
      // Download generated PDF
      try {
        await api.downloadKoreseddelPdf(data.id, data.filename || `${data.cargoRef}.pdf`);
      } catch {
        // Download failed, but generation succeeded
      }
      setSnackbar({ open: true, message: 'Køreseddel genereret', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Kunne ikke generere køreseddel', severity: 'error' });
    },
  });

  const bulkGenerateMutation = useMutation({
    mutationFn: ({ cargoIds, templateId }: { cargoIds: string[]; templateId: string }) =>
      api.bulkGenerateKoresedler(cargoIds, templateId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['koreseddel-cargos'] });
      queryClient.invalidateQueries({ queryKey: ['koresedler'] });
      setSelectedCargos(new Set());
      setSnackbar({
        open: true,
        message: `${data.generated} køresedler genereret${data.failed > 0 ? `, ${data.failed} fejlede` : ''}`,
        severity: data.failed > 0 ? 'error' : 'success',
      });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Bulk generering fejlede', severity: 'error' });
    },
  });

  const cmrGenerateMutation = useMutation({
    mutationFn: (cargoId: string) => api.generateCmr(cargoId),
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['koreseddel-cargos'] });
      try {
        await api.downloadCmrPdf(data.id, data.filename || `CMR-${data.cargoRef}.pdf`);
      } catch {
        // Download failed, but generation succeeded
      }
      setSnackbar({ open: true, message: `CMR fragtbrev genereret (${data.cargoRef})`, severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Kunne ikke generere CMR fragtbrev', severity: 'error' });
    },
  });

  const splitMutation = useMutation({
    mutationFn: ({ cargoId, reloadAddress, reloadDate }: { cargoId: string; reloadAddress: PlaceResult; reloadDate: string }) =>
      api.splitCargo(cargoId, {
        reloadAddress: {
          address: reloadAddress.address,
          city: reloadAddress.city,
          postcode: reloadAddress.postcode,
          country: reloadAddress.country,
          lat: reloadAddress.lat,
          lon: reloadAddress.lon,
          reloadDate,
        },
        // Note: trailerId not provided - routes will be created without trailer assignment
        // This can be enhanced later to include trailer selection in the split dialog
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['koreseddel-cargos'] });
      setSnackbar({
        open: true,
        message: data.newCargo1
          ? `Ordre delt! 1/3: ${data.newCargo1.ref}, 2/3: ${data.originalRef}, 3/3: ${data.newCargo3.ref}${data.tag2Applied ? ' (Tag 2 tilføjet)' : ''}`
          : `Re-split: ${data.originalRef} → nyt led: ${data.newCargo3.ref}`,
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Kunne ikke dele ordre',
        severity: 'error',
      });
    },
  });

  const unsplitMutation = useMutation({
    mutationFn: (cargoId: string) => api.unsplitCargo(cargoId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['koreseddel-cargos'] });
      setSnackbar({
        open: true,
        message: `Deling fortrudt! Original ordre: ${data.originalRef}. Slettet: ${data.deletedCargo2Ref}, ${data.deletedCargo3Ref}`,
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Kunne ikke fortryde deling',
        severity: 'error',
      });
    },
  });

  const syncDocumentsMutation = useMutation({
    mutationFn: (cargoId: string) => api.syncCargoDocuments(cargoId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['koreseddel-cargos'] });
      setSnackbar({
        open: true,
        message: `Dokumenter synkroniseret! ${data.documentsSynced} dokumenter kopieret på tværs af ${data.cargosInSplit} ordrer`,
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Kunne ikke synkronisere dokumenter',
        severity: 'error',
      });
    },
  });

  // Get unique types and customers for filters
  const uniqueTypes = useMemo(() => {
    if (!cargos) return [];
    const types = new Set(cargos.map(c => c.type).filter(Boolean));
    return Array.from(types).sort();
  }, [cargos]);

  const uniqueCustomers = useMemo(() => {
    if (!cargos) return [];
    const customers = new Set(cargos.map(c => c.customerName).filter(Boolean) as string[]);
    return Array.from(customers).sort();
  }, [cargos]);

  // Filter and sort cargos
  const filteredCargos = useMemo(() => {
    if (!cargos) return [];

    let result = [...cargos];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (cargo) =>
          cargo.ref.toLowerCase().includes(search) ||
          cargo.customerName?.toLowerCase().includes(search) ||
          cargo.invoiceRef?.toLowerCase().includes(search) ||
          cargo.description?.toLowerCase().includes(search)
      );
    }

    // Apply type filter
    if (typeFilter) {
      result = result.filter(cargo => cargo.type === typeFilter);
    }

    // Apply customer filter
    if (customerFilter) {
      result = result.filter(cargo => cargo.customerName === customerFilter);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: string | number | null = null;
      let bVal: string | number | null = null;

      switch (sortField) {
        case 'ref':
          aVal = a.ref;
          bVal = b.ref;
          break;
        case 'type':
          aVal = translateCargoType(a.type);
          bVal = translateCargoType(b.type);
          break;
        case 'description':
          aVal = a.description || '';
          bVal = b.description || '';
          break;
        case 'customerName':
          aVal = a.customerName || '';
          bVal = b.customerName || '';
          break;
        case 'invoiceRef':
          aVal = a.invoiceRef || '';
          bVal = b.invoiceRef || '';
          break;
        case 'from':
          aVal = a.firstStop?.city || '';
          bVal = b.firstStop?.city || '';
          break;
        case 'to':
          aVal = a.lastStop?.city || '';
          bVal = b.lastStop?.city || '';
          break;
        case 'date':
          aVal = a.firstStop?.date || '';
          bVal = b.firstStop?.date || '';
          break;
        case 'lines':
          aVal = a.invoiceLineCount;
          bVal = b.invoiceLineCount;
          break;
      }

      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal === null) return sortDirection === 'asc' ? -1 : 1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const comparison = String(aVal).localeCompare(String(bVal), 'da');
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [cargos, searchTerm, typeFilter, customerFilter, sortField, sortDirection]);

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, cargo: KoreseddelCargo) => {
    setMenuAnchor(event.currentTarget);
    setMenuCargo(cargo);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuCargo(null);
  };

  const handleGenerateWithTemplate = (templateId: string) => {
    if (menuCargo) {
      generateMutation.mutate({ cargoId: menuCargo.id, templateId });
    }
    handleMenuClose();
  };

  const handleGenerateCmr = () => {
    if (menuCargo) {
      cmrGenerateMutation.mutate(menuCargo.id);
    }
    handleMenuClose();
  };

  const handleBulkMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchor(null);
  };

  const handleBulkGenerate = (templateId: string) => {
    bulkGenerateMutation.mutate({
      cargoIds: Array.from(selectedCargos),
      templateId,
    });
    handleBulkMenuClose();
  };

  const handleOpenSplitDialog = (cargo: KoreseddelCargo) => {
    setSplitCargo(cargo);
    setSplitDialogOpen(true);
  };

  const handleCloseSplitDialog = () => {
    setSplitDialogOpen(false);
    setSplitCargo(null);
  };

  const handleSplitCargo = async (cargoId: string, reloadAddress: PlaceResult, reloadDate: string) => {
    await splitMutation.mutateAsync({ cargoId, reloadAddress, reloadDate });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCargos(new Set(filteredCargos.map((c) => c.id)));
    } else {
      setSelectedCargos(new Set());
    }
  };

  const handleSelectCargo = (cargoId: string, checked: boolean) => {
    const newSelected = new Set(selectedCargos);
    if (checked) {
      newSelected.add(cargoId);
    } else {
      newSelected.delete(cargoId);
    }
    setSelectedCargos(newSelected);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('da-DK');
  };

  const allSelected = filteredCargos.length > 0 && filteredCargos.every((c) => selectedCargos.has(c.id));
  const someSelected = selectedCargos.size > 0 && !allSelected;

  if (error) {
    return (
      <Alert severity="error">
        Kunne ikke hente ordrer. Prøv igen senere.
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Ordrer</Typography>
          {selectedCargos.size > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={handleBulkMenuOpen}
              disabled={bulkGenerateMutation.isPending}
            >
              {bulkGenerateMutation.isPending
                ? 'Genererer...'
                : `Generer (${selectedCargos.size})`}
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
              <TextField
                placeholder="Søg ref, kunde, faktura..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">Alle</MenuItem>
                  {uniqueTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {translateCargoType(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Kunde</InputLabel>
                <Select
                  value={customerFilter}
                  label="Kunde"
                  onChange={(e) => setCustomerFilter(e.target.value)}
                >
                  <MenuItem value="">Alle</MenuItem>
                  {uniqueCustomers.map(customer => (
                    <MenuItem key={customer} value={customer}>
                      {customer}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="Fra"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="Til"
                value={toDate}
                onChange={setToDate}
                slotProps={{ textField: { size: 'small' } }}
              />
              <Typography variant="body2" color="text.secondary">
                {filteredCargos.length} ordrer
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : filteredCargos.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Handlinger</TableCell>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'ref'}
                          direction={sortField === 'ref' ? sortDirection : 'asc'}
                          onClick={() => handleSort('ref')}
                        >
                          Ref
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'type'}
                          direction={sortField === 'type' ? sortDirection : 'asc'}
                          onClick={() => handleSort('type')}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'description'}
                          direction={sortField === 'description' ? sortDirection : 'asc'}
                          onClick={() => handleSort('description')}
                        >
                          Beskrivelse
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'customerName'}
                          direction={sortField === 'customerName' ? sortDirection : 'asc'}
                          onClick={() => handleSort('customerName')}
                        >
                          Kunde
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'invoiceRef'}
                          direction={sortField === 'invoiceRef' ? sortDirection : 'asc'}
                          onClick={() => handleSort('invoiceRef')}
                        >
                          Fakturaref
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'from'}
                          direction={sortField === 'from' ? sortDirection : 'asc'}
                          onClick={() => handleSort('from')}
                        >
                          Fra
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'to'}
                          direction={sortField === 'to' ? sortDirection : 'asc'}
                          onClick={() => handleSort('to')}
                        >
                          Til
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'date'}
                          direction={sortField === 'date' ? sortDirection : 'asc'}
                          onClick={() => handleSort('date')}
                        >
                          Dato
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        <TableSortLabel
                          active={sortField === 'lines'}
                          direction={sortField === 'lines' ? sortDirection : 'asc'}
                          onClick={() => handleSort('lines')}
                        >
                          Linjer
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Tidsregistrering">
                          <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">Køreseddel</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCargos.map((cargo) => (
                      <TableRow key={cargo.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {isSplitCargo(cargo.note) ? (
                              <>
                                {!isArchiveCopy(cargo.note) && (
                                  <Tooltip title="Split igen">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenSplitDialog(cargo)}
                                      disabled={splitMutation.isPending}
                                    >
                                      <CallSplitIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Fortryd deling">
                                  <IconButton
                                    size="small"
                                    onClick={() => unsplitMutation.mutate(cargo.id)}
                                    disabled={unsplitMutation.isPending}
                                    color="warning"
                                  >
                                    <CallMergeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Synkroniser dokumenter">
                                  <IconButton
                                    size="small"
                                    onClick={() => syncDocumentsMutation.mutate(cargo.id)}
                                    disabled={syncDocumentsMutation.isPending}
                                    color="info"
                                  >
                                    <SyncIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title="Split ordre">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenSplitDialog(cargo)}
                                  disabled={splitMutation.isPending}
                                >
                                  <CallSplitIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Generer køreseddel">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, cargo)}
                                disabled={generateMutation.isPending}
                              >
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedCargos.has(cargo.id)}
                            onChange={(e) => handleSelectCargo(cargo.id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {cargo.ref}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={translateCargoType(cargo.type)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={cargo.description || ''} arrow>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                              {cargo.description || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                            {cargo.customerName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                            {cargo.invoiceRef || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {cargo.firstStop?.city || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {cargo.lastStop?.city || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(cargo.firstStop?.date)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color={cargo.invoiceLineCount > 0 ? 'text.primary' : 'text.disabled'}>
                            {cargo.invoiceLineCount || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {cargo.hasTimeTracking ? (
                            <Tooltip title="Tidsregistrering findes">
                              <AccessTimeIcon fontSize="small" color="success" />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {cargo.hasKoreseddel ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <Tooltip title={`Genereret: ${cargo.koreseddelInfo?.uploadedToDora ? 'Uploadet til DORA' : 'Ikke uploadet'}`}>
                                <Chip
                                  icon={cargo.koreseddelInfo?.uploadedToDora ? <CloudUploadIcon /> : <CheckCircleIcon />}
                                  label={cargo.koreseddelInfo?.uploadedToDora ? 'Uploadet' : 'Genereret'}
                                  size="small"
                                  color={cargo.koreseddelInfo?.uploadedToDora ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </Tooltip>
                              <Tooltip title="Download PDF">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    if (cargo.koreseddelInfo?.id) {
                                      try {
                                        await api.downloadKoreseddelPdf(cargo.koreseddelInfo.id, `${cargo.ref}.pdf`);
                                      } catch {
                                        setSnackbar({ open: true, message: 'Kunne ikke downloade PDF', severity: 'error' });
                                      }
                                    }
                                  }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={6}>
                <Typography color="text.secondary">
                  Ingen ordrer fundet i den valgte periode
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Template Selection Menu (single cargo) */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{ sx: { maxWidth: 350, maxHeight: 500 } }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
            Vælg skabelon
          </Typography>
          <Divider />
          {templates?.map((template: KoreseddelTemplate) => (
            <MenuItem
              key={template.id}
              onClick={() => handleGenerateWithTemplate(template.id)}
            >
              <ListItemIcon>
                <DescriptionIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={template.name}
                secondary={template.description}
                secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
              />
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={handleGenerateCmr} disabled={cmrGenerateMutation.isPending}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText
              primary="CMR Fragtbrev"
              secondary="Generer internationalt fragtbrev"
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
          </MenuItem>
        </Menu>

        {/* Bulk Template Selection Menu */}
        <Menu
          anchorEl={bulkMenuAnchor}
          open={Boolean(bulkMenuAnchor)}
          onClose={handleBulkMenuClose}
          PaperProps={{ sx: { maxWidth: 350, maxHeight: 500 } }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
            Vælg skabelon til {selectedCargos.size} ordrer
          </Typography>
          <Divider />
          {templates?.map((template: KoreseddelTemplate) => (
            <MenuItem
              key={template.id}
              onClick={() => handleBulkGenerate(template.id)}
            >
              <ListItemIcon>
                <DescriptionIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={template.name}
                secondary={template.description}
                secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
              />
            </MenuItem>
          ))}
        </Menu>

        {/* Split Cargo Dialog */}
        <SplitCargoDialog
          open={splitDialogOpen}
          onClose={handleCloseSplitDialog}
          cargo={splitCargo}
          onSplit={handleSplitCargo}
          isLoading={splitMutation.isPending}
        />

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
